import { useSessionManagementServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  toActivityMetrics,
  toApiExternalMetricFilter,
} from "../../../../domain/mappers/metricsMappers";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";
import {
  ExternalActivityMetricsResult,
  ExternalMetricsParams,
} from "./externalMetricsTypes";

export const ALL_AGGREGATE_EXTERNAL_METRICS_QUERY_KEY =
  "allAggregateExternalMetrics";

export const useAllAggregateExternalMetrics = ({
  startDate,
  endDate,
  dimension,
  licenseIds,
  minSessionDurationInMinutes,
}: ExternalMetricsParams): ExternalActivityMetricsResult => {
  const sessionManagementService = useSessionManagementServiceV1();

  const { companyId } = useParams();

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [
      ALL_AGGREGATE_EXTERNAL_METRICS_QUERY_KEY,
      companyId,
      startDate.toISOString(),
      endDate.toISOString(),
      dimension,
      licenseIds,
      minSessionDurationInMinutes,
    ],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const filter = toApiExternalMetricFilter({
        startDate,
        endDate,
        dimension,
        licenseIds,
        minSessionDurationInMinutes,
      });

      const result =
        await sessionManagementService.getAllAggregateExternalMetrics(
          companyId,
          filter,
        );

      return toActivityMetrics(result);
    },
    enabled: !!companyId && licenseIds.length > 0,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  if (licenseIds.length === 0) {
    return {
      data: undefined,
      isLoading: false,
      error: null,
      isFetching: false,
    };
  }

  return {
    data,
    isLoading,
    isFetching,
    error: error ?? null,
  };
};
